/**
 * Windows-1253 (Greek) codec with strict error reporting.
 *
 * The legacy autocue export is Windows-1253; `iconv-lite` would silently write `?`
 * for characters outside the code page, which the brief forbids. This codec reports
 * every unsupported character with its position, line and column instead.
 */
import { CP1253_HIGH } from './cp1253-table.js';
export class EncodingError extends Error {
    unsupported;
    encoding = 'windows-1253';
    constructor(unsupported) {
        super(`${unsupported.length} character(s) cannot be encoded in windows-1253: ` +
            unsupported
                .slice(0, 5)
                .map((u) => `U+${u.codePoint.toString(16).toUpperCase().padStart(4, '0')} "${u.char}" at line ${u.line}, column ${u.column}`)
                .join('; ') +
            (unsupported.length > 5 ? '; ...' : ''));
        this.unsupported = unsupported;
        this.name = 'EncodingError';
    }
}
export class DecodingError extends Error {
    offset;
    byte;
    encoding = 'windows-1253';
    constructor(offset, byte) {
        super(`byte 0x${byte.toString(16).toUpperCase().padStart(2, '0')} at offset ${offset} is undefined in windows-1253`);
        this.offset = offset;
        this.byte = byte;
        this.name = 'DecodingError';
    }
}
const ENCODE = new Map();
for (let i = 0; i < 128; i++) {
    const cp = CP1253_HIGH[i];
    if (cp >= 0)
        ENCODE.set(cp, 0x80 + i);
}
/** Decode bytes. Undefined bytes throw unless `lenient`, in which case they become U+FFFD. */
export function decodeCp1253(bytes, opts = {}) {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i];
        if (b < 0x80) {
            out += String.fromCharCode(b);
            continue;
        }
        const cp = CP1253_HIGH[b - 0x80];
        if (cp < 0) {
            if (!opts.lenient)
                throw new DecodingError(i, b);
            out += '�';
        }
        else
            out += String.fromCodePoint(cp);
    }
    return out;
}
/** Every character of `text` that has no windows-1253 byte, with its position. */
export function findUnsupportedCp1253(text) {
    const result = [];
    let line = 1;
    let column = 1;
    for (let i = 0; i < text.length;) {
        const cp = text.codePointAt(i);
        const len = cp > 0xffff ? 2 : 1;
        if (!(cp < 0x80 || ENCODE.has(cp))) {
            result.push({ index: i, line, column, char: String.fromCodePoint(cp), codePoint: cp });
        }
        if (cp === 0x0a) {
            line++;
            column = 1;
        }
        else if (cp !== 0x0d)
            column++;
        i += len;
    }
    return result;
}
/** Encode strictly: throws `EncodingError` listing every unsupported character. */
export function encodeCp1253(text) {
    const bad = findUnsupportedCp1253(text);
    if (bad.length)
        throw new EncodingError(bad);
    const out = new Uint8Array(text.length);
    let n = 0;
    for (let i = 0; i < text.length; i++) {
        const cp = text.charCodeAt(i);
        out[n++] = cp < 0x80 ? cp : ENCODE.get(cp);
    }
    return out.subarray(0, n);
}
/** True when every character of `text` is representable. */
export function isCp1253Representable(text) {
    return findUnsupportedCp1253(text).length === 0;
}
//# sourceMappingURL=cp1253.js.map